import React, { useState } from 'react';
import { Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { FORM_FILE_TYPES, getFormFileError } from '@/utils/formFilePolicy';
import { assetToSubmission } from '@/utils/formAssets';
import FileInput from '@/components/General/inputs/FileInput';
import FieldLabel from './FieldLabel';

/**
 * Renders a `file` schema field. Reuses `FileInput` for picking + display, then
 * converts selected assets to `{ filename, mimeType, base64, size }` objects for
 * submission. Supports `allowMultiple` from the field config.
 */
const FileField = ({ field, value, onChange, disabled }) => {
    const config = field.config || {};
    const allowMultiple = Boolean(config.allowMultiple);
    const [processing, setProcessing] = useState(false);
    const [selectionError, setSelectionError] = useState(null);

    const assets = allowMultiple
        ? (Array.isArray(value) ? value : (value ? [value] : []))
        : (value || null);

    const handleSelect = async (picked) => {
        if(disabled || processing) return;
        const pickedAssets = Array.isArray(picked) ? picked : [picked];
        const error = pickedAssets.map(asset => getFormFileError(asset, config)).find(Boolean);
        setSelectionError(error || null);
        if(error) return;
        setProcessing(true);
        try {
            const converted = [];
            for(const asset of pickedAssets){
                converted.push(await assetToSubmission(asset));
            }
            if(allowMultiple){
                onChange([...(Array.isArray(value) ? value : []), ...converted]);
            } else {
                onChange(converted[0] ?? null);
            }
        } catch {
            setSelectionError('Unable to read this file. Please select it again.');
        } finally {
            setProcessing(false);
        }
    };

    const handleRemove = (_uri, index) => {
        if(disabled || processing) return;
        setSelectionError(null);
        if(allowMultiple){
            const next = (Array.isArray(value) ? value : []).filter((_, i) => i !== index);
            onChange(next);
        } else {
            onChange(null);
        }
    };

    return (
        <>
            <FieldLabel
                label={field.label}
                required={field.required}
                description={field.description}
                testID={`form-field-${field.fieldId}-label`}
            />
            <FileInput
                testID={`form-field-${field.fieldId}`}
                title={processing ? 'Processing…' : undefined}
                assets={assets}
                multiple={allowMultiple}
                disabled={disabled || processing}
                acceptedTypes={[...new Set(Object.values(FORM_FILE_TYPES).flat())]}
                onSelect={handleSelect}
                onRemoveAsset={handleRemove}
            />
            {!!selectionError && <Text style={styles.error} testID={`form-field-${field.fieldId}-file-error`}>{selectionError}</Text>}
        </>
    );
};

const styles = StyleSheet.create(({ colors, fonts, typography }) => ({
    error: { color: colors.error, fontFamily: fonts.body, fontSize: typography.caption, paddingTop: 4 },
}));

export default FileField;
